import { useState } from 'react';
import { QRCodeSVG } from 'qrcode.react';
import { Alert, api, Badge, CopyButton, ErrorAlert, formatTime, PageHeader, Spinner, useApi, useAsyncAction } from '@pryme/ui';
import { useDev } from '../lib/context';
import { ROLE_LABEL, ROLE_RANK, ROLES, type Member, type Role } from '../lib/types';

const ROLE_DESC: Record<Role, string> = {
  owner: 'Everything, including membership changes. At least one owner must remain.',
  admin: 'Organization domains, redirect URIs, claim and security settings, credentials (confirmed with a passkey) and invitations below admin.',
  developer: 'Register services, request activation challenges and read everything.',
  read_only: 'View services, public key material, events and settings.',
};

/** Roles the current member may grant: strictly below their own (owners may grant owner). */
export function grantableRoles(mine: Role): Role[] {
  return ROLES.filter((r) => ROLE_RANK[r] < ROLE_RANK[mine] || (mine === 'owner' && r === 'owner'));
}

function MemberRow(props: { m: Member; isOwner: boolean; orgId: string; onChanged(): void }) {
  const { m } = props;
  const change = useAsyncAction();
  const [confirmRemove, setConfirmRemove] = useState(false);
  const set = (role: Role | 'removed') =>
    change.run(async () => {
      await api(`/v1/orgs/${props.orgId}/members/${encodeURIComponent(m.memberRef)}`, { method: 'PATCH', body: { role } });
      props.onChanged();
    });
  return (
    <tr>
      <td>
        <code className="p-mono-chip">{m.memberRef}</code> {m.you && <Badge tone="brand">you</Badge>}
      </td>
      <td>
        {props.isOwner ? (
          <select className="p-select" aria-label={`Role for ${m.memberRef}`} value={m.role} disabled={change.busy} onChange={(e) => void set(e.target.value as Role)} style={{ minWidth: 140 }}>
            {ROLES.map((r) => <option key={r} value={r}>{ROLE_LABEL[r]}</option>)}
          </select>
        ) : (
          <Badge tone="neutral">{ROLE_LABEL[m.role]}</Badge>
        )}
      </td>
      <td>{formatTime(m.createdAt)}</td>
      <td>
        {props.isOwner && (confirmRemove ? (
          <div className="p-row">
            <button className="p-btn danger small" disabled={change.busy} onClick={() => void set('removed')}>Confirm remove</button>
            <button className="p-btn ghost small" onClick={() => setConfirmRemove(false)}>Cancel</button>
          </div>
        ) : (
          <button className="p-btn danger-outline small" onClick={() => setConfirmRemove(true)}>{m.you ? 'Leave…' : 'Remove…'}</button>
        ))}
        <ErrorAlert error={change.error} />
      </td>
    </tr>
  );
}

export function Team() {
  const { org, reloadOrgs } = useDev();
  const members = useApi<{ members: Member[] }>(org ? `/v1/orgs/${org.id}/members` : null, [org?.id]);
  const [role, setRole] = useState<Role>('developer');
  const [invite, setInvite] = useState<{ url: string; role: Role; expiresInDays: number } | null>(null);
  const create = useAsyncAction();

  if (!org) return <Alert tone="info">Create or select an organization first.</Alert>;
  const isOwner = org.role === 'owner';
  const grantable = grantableRoles(org.role);
  const canInvite = ROLE_RANK[org.role] >= ROLE_RANK.admin;

  return (
    <>
      <PageHeader
        eyebrow={`Organization · ${org.display_name}`}
        title="Team"
        lead="Members are people with an active Pryme ID. Each member is shown by a pseudonymous reference: Pryme does not reveal teammates’ handles or identities, even to owners."
      />

      <section className="p-card" aria-labelledby="mem-h">
        <h2 id="mem-h">Members</h2>
        <ErrorAlert error={members.error} />
        {members.loading && !members.data ? <Spinner label="Loading members…" /> : (
          <div className="p-table-wrap">
            <table className="p-table">
              <thead><tr><th>Member</th><th>Role</th><th>Joined</th><th><span className="p-skip">Actions</span></th></tr></thead>
              <tbody>
                {members.data?.members.map((m) => (
                  <MemberRow key={m.memberRef} m={m} isOwner={isOwner} orgId={org.id} onChanged={() => { void members.reload(); void reloadOrgs(); }} />
                ))}
              </tbody>
            </table>
          </div>
        )}
        {!isOwner && <p className="p-small p-muted">Only owners can change roles or remove members.</p>}
      </section>

      <section className="p-card" aria-labelledby="inv-h">
        <h2 id="inv-h">Invite a teammate</h2>
        {canInvite ? (
          <form
            className="p-row"
            style={{ alignItems: 'flex-end' }}
            onSubmit={async (e) => {
              e.preventDefault();
              const r = await create.run(() => api<{ url: string; role: Role; expiresInDays: number }>(`/v1/orgs/${org.id}/invites`, { body: { role } }));
              if (r) setInvite(r);
            }}
          >
            <div className="p-field" style={{ marginBottom: 0, minWidth: 200 }}>
              <label htmlFor="inv-role">Role</label>
              <select id="inv-role" className="p-select" value={role} onChange={(e) => setRole(e.target.value as Role)}>
                {grantable.map((r) => <option key={r} value={r}>{ROLE_LABEL[r]}</option>)}
              </select>
            </div>
            <button className="p-btn" disabled={create.busy || !grantable.includes(role)}>{create.busy ? <Spinner /> : 'Create invite link'}</button>
          </form>
        ) : (
          <p className="p-muted">Admins and owners can invite. Your role: {ROLE_LABEL[org.role]}.</p>
        )}
        <p className="p-small p-muted" style={{ marginTop: '0.5rem' }}>
          No role escalation: you can only grant roles below your own (owners may also grant owner). The invitee must sign in with an active Pryme ID to accept.
        </p>
        <ErrorAlert error={create.error} />
        {invite && (
          <div className="p-card muted" style={{ marginTop: '1rem' }}>
            <h3>Single-use invite · {ROLE_LABEL[invite.role]} · valid {invite.expiresInDays} days</h3>
            <div className="p-row" style={{ alignItems: 'flex-start' }}>
              <QRCodeSVG value={invite.url} size={128} aria-label="QR code of the invite link" />
              <div style={{ flex: 1, minWidth: 220 }}>
                <div className="p-secret">{invite.url}</div>
                <div className="p-row" style={{ marginTop: '0.5rem' }}><CopyButton value={invite.url} label="Copy link" /></div>
                <p className="p-small p-muted">Share it privately. Anyone with the link and an active Pryme ID can join with this role, once.</p>
              </div>
            </div>
          </div>
        )}
      </section>

      <section className="p-card" aria-labelledby="roles-h">
        <h2 id="roles-h">Roles</h2>
        <dl className="p-kv">
          {ROLES.map((r) => (
            <div key={r} style={{ display: 'contents' }}>
              <dt><strong>{ROLE_LABEL[r]}</strong></dt>
              <dd>{ROLE_DESC[r]}</dd>
            </div>
          ))}
        </dl>
      </section>
    </>
  );
}
